import React, { useState, useEffect } from 'react';
import { X, Calendar, Tag, AlertCircle, FileText, FolderPlus, Building2, DollarSign, Package } from 'lucide-react';
import { Project, CreateProjectData, useProjectsStore } from '../../store/projectsStore';
import { contractsService, Contract } from '../../services/contracts.service';
import { projectsService } from '../../services/projects.service';

type CreationType = 'individual' | 'from_contract';

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project?: Project | null;
  preselectedContractId?: string; // For creating from ViewContractModal
}

const AddProjectModal: React.FC<AddProjectModalProps> = ({
  isOpen,
  onClose,
  project,
  preselectedContractId
}) => {
  const { createProject, updateProject, fetchProjects } = useProjectsStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Creation type state
  const [creationType, setCreationType] = useState<CreationType>('individual');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);

  const [formData, setFormData] = useState<CreateProjectData>({
    title: '',
    description: '',
    status: 'draft',
    priority: 'medium',
    tags: [],
  });

  const [tagInput, setTagInput] = useState('');

  // Fetch active contracts when creation type changes
  useEffect(() => {
    if (isOpen && creationType === 'from_contract') {
      setIsLoadingContracts(true);
      contractsService.getAll()
        .then((allContracts: Contract[]) => {
          // Filter to only active/signed contracts that have deliverables
          const eligibleContracts = allContracts.filter((c: Contract) =>
            ['active', 'signed'].includes(c.status) && c.deliverables && c.deliverables.length > 0
          );
          setContracts(eligibleContracts);

          // Pre-select contract if provided
          if (preselectedContractId && eligibleContracts.find((c: Contract) => c.id === preselectedContractId)) {
            setSelectedContractId(preselectedContractId);
          }
        })
        .catch((err: Error) => console.error('Failed to fetch contracts:', err))
        .finally(() => setIsLoadingContracts(false));
    }
  }, [isOpen, creationType, preselectedContractId]);

  // Initialize creation type if preselected contract provided
  useEffect(() => {
    if (preselectedContractId && isOpen) {
      setCreationType('from_contract');
      setSelectedContractId(preselectedContractId);
    }
  }, [preselectedContractId, isOpen]);

  useEffect(() => {
    if (project) {
      setCreationType('individual');
      setFormData({
        title: project.title,
        description: project.description || '',
        status: project.status,
        priority: project.priority,
        ...(project.deadline && { deadline: project.deadline }),
        tags: project.tags || [],
      });
    } else {
      setFormData({
        title: '',
        description: '',
        status: 'draft',
        priority: 'medium',
        tags: [],
      });
    }
    setTagInput('');
    setErrors({});
    if (!preselectedContractId) {
      setSelectedContractId('');
    }
  }, [project, isOpen, preselectedContractId]);

  if (!isOpen) return null;

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.tags?.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), trimmedTag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // For from_contract mode, only need to validate contract selection
    if (creationType === 'from_contract') {
      if (!selectedContractId) {
        newErrors.contract = 'Please select a contract';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // For individual mode, validate title
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.deadline) {
      const deadline = new Date(formData.deadline);
      if (isNaN(deadline.getTime())) {
        newErrors.deadline = 'Invalid date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      if (creationType === 'from_contract' && selectedContractId) {
        // Create project from contract - tasks auto-created
        await projectsService.createProjectFromContract(selectedContractId);
        await fetchProjects(); // Refresh projects list
        onClose();
      } else if (project) {
        await updateProject({
          id: project.id,
          ...formData
        });
        onClose();
      } else {
        await createProject(formData);
        onClose();
      }
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get selected contract details for display
  const selectedContract = contracts.find(c => c.id === selectedContractId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {project ? 'Edit Project' : 'Create New Project'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Creation Type Selector - only show for new projects */}
              {!project && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    How would you like to create this project?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCreationType('individual')}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${creationType === 'individual'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                    >
                      <FolderPlus className={`w-6 h-6 ${creationType === 'individual' ? 'text-primary-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <div className={`font-medium ${creationType === 'individual' ? 'text-primary-600' : 'text-gray-900 dark:text-gray-100'}`}>
                          Individual Project
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Start from scratch</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreationType('from_contract')}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${creationType === 'from_contract'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                    >
                      <FileText className={`w-6 h-6 ${creationType === 'from_contract' ? 'text-primary-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <div className={`font-medium ${creationType === 'from_contract' ? 'text-primary-600' : 'text-gray-900 dark:text-gray-100'}`}>
                          From Contract
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Import deliverables as tasks</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* From Contract: Contract Selector */}
              {creationType === 'from_contract' && !project && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Contract <span className="text-red-500">*</span>
                    </label>
                    {isLoadingContracts ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        <span className="ml-2 text-gray-500">Loading contracts...</span>
                      </div>
                    ) : contracts.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                        <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 dark:text-gray-400">No active contracts with deliverables found</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Create a contract with deliverables first</p>
                      </div>
                    ) : (
                      <select
                        value={selectedContractId}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors ${errors.contract ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                          }`}
                      >
                        <option value="">Select a contract...</option>
                        {contracts.map(contract => (
                          <option key={contract.id} value={contract.id}>
                            {contract.title} ({contract.deliverables?.length || 0} deliverables)
                          </option>
                        ))}
                      </select>
                    )}
                    {errors.contract && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.contract}
                      </p>
                    )}
                  </div>

                  {/* Selected Contract Preview */}
                  {selectedContract && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-primary-500" />
                        <span className="font-medium text-gray-900 dark:text-white">{selectedContract.title}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-500" />
                          <span className="text-gray-600 dark:text-gray-300">${selectedContract.totalValue?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-blue-500" />
                          <span className="text-gray-600 dark:text-gray-300">{selectedContract.deliverables?.length || 0} deliverables → tasks</span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          A project will be created with tasks auto-generated from the contract deliverables.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Individual Project Fields */}
              {(creationType === 'individual' || project) && (
                <>
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors ${errors.title ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                        }`}
                      placeholder="Enter project title"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none transition-colors"
                      placeholder="Describe the project..."
                    />
                  </div>

                  {/* Status and Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Project['status'] }))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="on_hold">On Hold</option>
                        <option value="completed">Completed</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as Project['priority'] }))}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  {/* Deadline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Deadline
                    </label>
                    <input
                      type="date"
                      value={formData.deadline ? new Date(formData.deadline).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        ...(e.target.value ? { deadline: e.target.value } : {})
                      }))}
                      className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors ${errors.deadline ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                        }`}
                    />
                    {errors.deadline && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.deadline}
                      </p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Tag className="w-4 h-4 inline mr-1" />
                      Tags
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        placeholder="Add a tag and press Enter"
                        className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {formData.tags && formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-sm"
                          >
                            #{tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="hover:text-primary-900 dark:hover:text-primary-200"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || (creationType === 'from_contract' && !selectedContractId)}
              className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {isSubmitting
                ? 'Creating...'
                : creationType === 'from_contract'
                  ? 'Create from Contract'
                  : project
                    ? 'Update Project'
                    : 'Create Project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;
